import { StandardError } from "@core-ts/StandardError"

import Long from "long"
import { TimeStamp } from "../TimeStamp"
import { context } from "../Contexts/Context"
import { Variant } from "../Variant"
import { VariantClassless } from "../Variant"
import { QueryIterator } from "../QueryIterator"
import { NodeID } from "@core-ts/Node"
import { BinaryRep } from "@core-ts/BinaryRep"

// This file must be compatible with a browser

export type TimeStampNativeType = number | Long | null
export type NativeValueType = string | number | TimeStampNativeType | null | BinaryRep

// Query
export interface ResultHeader {
    totalCount: number
    resultCount: number
    fields: ResultFieldMetaData[]
    subNodes: SubNodeMetaData[] | undefined
}

export interface QueryRequest {
    queryString: string
    requestedFields: string[]
}

export interface QueryVRequest {
    requests: QueryRequest[]
}

export interface QueryResponse {
    meta: ResultHeader
    rows: ResultRow[]
}

export interface QueryVResponse {
    responses: QueryResponse[]
}

export interface ResultRow {
    [key: string]: NativeValueType
}

export enum DataType {
    ID = 1,
    Path = 2,
    String = 3,
    Integer = 4,
    Double = 5,
    TimeStamp = 6,
    Folder = 7,
    Binary = 8,
}

export interface ResultFieldMetaData {
    fieldName: string
    fieldType: DataType
    readOnly: boolean // Can the user modify this value?
    calculated: boolean // If field is calculated, readOnly will also be true
    accessToModify: boolean // If user has not access to modify, readOnly will also be set
    nullAllowed: boolean // Is NULL allowed?
    allowedValues: string[] | undefined
}
export type HeaderCallback = (meta: ResultHeader) => Promise<void>
export type ResultCallback = (resultMeta: ResultHeader, row: ResultRow) => Promise<void>
export type ResultCallbackMultiple = (resultMeta: ResultHeader, row: ResultRow[]) => Promise<void>

export interface SubNodeMetaData {
    name: string
    objectTypes: string[] | undefined
}

export class ResultRowVariant {
    [key: string]: Variant
}

export class ResultSetVariant {
    header: ResultHeader
    rows: ResultRowVariant[] = []

    constructor(header: ResultHeader) {
        this.header = header
    }
}

export class ResultRowVariantClassless {
    [key: string]: VariantClassless
}

// Modify request
export interface ModifyRequest {
    /* TODO: Replace this string with a union string literal type with all the available methods
     * It will be a bit tricky to make the same list propogate down through GRPC to implementation,
     * but we can atleast help out when implementing this.
     */
    method: string
    selectorString: string
    modificationRows?: ResultRowVariant[]
}

export interface ModifyVRequest {
    requests: ModifyRequest[]
}

// This is used when we do a request over the web since that won't properly send the class implementation
export interface ModifyRequestClassless {
    method: string
    selectorString: string
    modificationRows: ResultRowVariantClassless[]
}

export interface ModifyVRequestClassless {
    requests: ModifyRequestClassless[]
}

export interface ModifyResponse {
    resultingPath?: string
    resultingId?: number
    objectWasCreated?: boolean
}

export interface ModifyVResponse {
    responses: ModifyResponse[]
}

export function convertNativeRowToVariantRow(meta: ResultHeader, row: ResultRow): ResultRowVariant {
    const untypedResultRow = new ResultRowVariant()
    let fieldNumber = 0
    for (const [key, value] of Object.entries(row)) {
        if (!Array.isArray(meta.fields) || meta.fields[fieldNumber] === undefined)
            throw new StandardError("Couldn't find meta data for field")
        untypedResultRow[key] = Variant.fromNativeType(value, meta.fields[fieldNumber].fieldType)
        fieldNumber++
    }
    return untypedResultRow
}

function ResultCallbackMultipleToResultCallback(rowCallback: ResultCallback): ResultCallbackMultiple {
    return async (meta: ResultHeader, rows: ResultRow[]): Promise<void> => {
        for (const row of rows) {
            await rowCallback(meta, row)
        }
    }
}

/*export class StorageModifications
{
    modifications: ModifyRequest[] = []

    InsertIfNotExists(selectorString: string, rows?: ResultRowVariant|ResultRowVariant[]): void {
        this.modifications.push({
            method: "InsertIfNotExists",
            selectorString,
            modificationRows: rows === undefined ? undefined : (Array.isArray(rows) ? rows : [rows])
        })
    }

    commit(): void {
        context.storage.ModifyV(this.modifications)
    }
}*/

export abstract class StorageServiceClient {
    DeleteLinkedRow(arg0: string, path: string) {
        throw new Error("Method not implemented.")
    }
    SetObject: any
    DeleteObject(arg0: string) {
        throw new Error("Method not implemented.")
    }
    QueryUntypedGetAll(arg0: { queryString: string; requestedFields: string[] }) {
        throw new Error("Method not implemented.")
    }
    protected abstract connect(host: string, credentials: string): Promise<void>

    abstract QueryV(
        requests: QueryRequest[],
        rowCallbacks: ResultCallbackMultiple[],
        metaCallback: HeaderCallback[],
    ): Promise<void>

    abstract ModifyV(requests: ModifyRequest[]): Promise<ModificationResult[]>

    // Helpers for convinience
    async QueryUntypedCallback(
        request: QueryRequest,
        rowCallback: ResultCallback,
        metaCallback: HeaderCallback = async () => {},
    ): Promise<void> {
        // This is a good place to check if we are cancelled and abort processing if we are
        if(context.haveRequest)
            context.request.throwIfCancelled()
 
        return await this.QueryV([request], [ResultCallbackMultipleToResultCallback(rowCallback)], [metaCallback])
    }

    async Modify(modifyRequest: ModifyRequest): Promise<ModifyResponse> {
        const responses = await this.ModifyV([modifyRequest])

        if (responses.length !== 1) throw new StandardError("Expected exactly one response to Modify request")

        return responses[0]
    }

    async InsertRow(
        objectInterface: string,
        modificationRow: ResultRowVariant,
        selectorString: string = "",
    ): Promise<NodeID> {
        const modifyRequest: ModifyRequest = {
            method: "InsertRow",
            selectorString,
            modificationRows: [
                {
                    ObjectInterface: Variant.fromString(objectInterface),
                    ...modificationRow,
                },
            ],
        }

        const result = await context.storage.Modify(modifyRequest)
        if (result.resultingId === undefined) throw new StandardError("Expected resultingId to be set")

        return result.resultingId
    }

    async UpdateRow(selectorString: string, modificationRow: ResultRowVariant): Promise<ModifyResponse> {
        return await context.storage.Modify({
            method: "UpdateRow",
            selectorString: selectorString,
            modificationRows: [modificationRow],
        })
    }

    async InsertOrUpdateRow(selectorString: string, modificationRow: ResultRowVariant): Promise<ModifyResponse> {
        const modifyRequest: ModifyRequest = {
            method: "InsertOrUpdateRow",
            selectorString: selectorString,
            modificationRows: [modificationRow],
        }

        return await context.storage.Modify(modifyRequest)
    }

    async RemoveRow(selectorString: string): Promise<ModifyResponse> {
        const modifyRequest: ModifyRequest = {
            method: "RemoveRow",
            selectorString: selectorString,
        }

        return await context.storage.Modify(modifyRequest)
    }

    async QueryCallback<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification,
        rowCallback: (meta: ResultHeader, row: RowType) => Promise<void>,
    ): Promise<void> {
        // Note, this will fail if we use a minifier. We have our minifier set to not mangle class names for this reason
        let objectInterface = allocator.name

        if (objectInterface === "Node") objectInterface = "Folder"

        const emptyObject = new allocator()
        const queryString = expandQuerySpecification(querySpecification, objectInterface)

        const requestedFields: string[] = []
        for (const [field, fieldValue] of Object.entries(emptyObject)) {
            if (isFieldType(fieldValue)) requestedFields.push(field)
        }

        const fieldTypes = new Map<string, DataType | keyof typeof DataType>()

        const queryRequest = {
            queryString,
            requestedFields,
        }

        return this.QueryUntypedCallback(
            queryRequest,
            async (meta: ResultHeader, row: ResultRow) => {
                const resultRow = new allocator()
                resultRow._internalData.objectInterface = objectInterface

                let fieldCounter = 0
                for (let fieldValue of Object.values(resultRow)) {
                    if (!isFieldType(fieldValue)) continue

                    const fieldType = meta.fields[fieldCounter].fieldType
                    const fieldName = meta.fields[fieldCounter].fieldName
                    const nullAllowed = meta.fields[fieldCounter].nullAllowed
                    fieldValue = row[fieldName]
                    fieldCounter++
                    switch (fieldType) {
                        case DataType.ID:
                            {
                                if (fieldValue !== null && typeof fieldValue !== "number")
                                    throw new StandardError("Received ResultRowNative of the wrong type")
                                // The compiler can't see that since we are iterating through the fields of the actual resultRow,
                                // this field must exist. Hence the ts-ignore
                                // @ts-ignore
                                resultRow[fieldName] = fieldValue
                                resultRow._internalData.originalValues[fieldName] = fieldValue
                            }
                            break
                        case DataType.Path:
                            {
                                if (fieldValue !== null && typeof fieldValue !== "string")
                                    throw new StandardError("Received ResultRowNative of the wrong type")
                                // @ts-ignore
                                resultRow[fieldName] = fieldValue
                                resultRow._internalData.originalValues[fieldName] = fieldValue
                            }
                            break
                        case DataType.String:
                            {
                                if (fieldValue !== null && typeof fieldValue !== "string")
                                    throw new StandardError("Received ResultRowNative of the wrong type")
                                // @ts-ignore
                                resultRow[fieldName] = fieldValue
                                resultRow._internalData.originalValues[fieldName] = fieldValue
                            }
                            break
                        case DataType.Integer:
                            {
                                if (fieldValue !== null && typeof fieldValue !== "number")
                                    throw new StandardError("Received ResultRowNative of the wrong type")
                                // @ts-ignore
                                resultRow[fieldName] = fieldValue
                                resultRow._internalData.originalValues[fieldName] = fieldValue
                            }
                            break
                        case DataType.Double:
                            {
                                if (fieldValue !== null && typeof fieldValue !== "number")
                                    throw new StandardError("Received ResultRowNative of the wrong type")
                                // @ts-ignore
                                resultRow[fieldName] = fieldValue
                                resultRow._internalData.originalValues[fieldName] = fieldValue
                            }
                            break
                        case DataType.TimeStamp:
                            {
                                if (fieldValue === null) {
                                    // @ts-ignore
                                    resultRow[fieldName] = null
                                    resultRow._internalData.originalValues[fieldName] = null
                                } else {
                                    if (
                                        fieldValue !== null &&
                                        typeof fieldValue !== "number" &&
                                        !Long.isLong(fieldValue)
                                    )
                                        throw new StandardError("Received ResultRowNative of the wrong type")
                                    const timeStamp = TimeStamp.fromUnixTimestamp(fieldValue)
                                    // @ts-ignore
                                    resultRow[fieldName] = timeStamp
                                    resultRow._internalData.originalValues[fieldName] = fieldValue
                                }
                            }
                            break

                        case DataType.Binary: {
                            /*                            const parsedJSON = JSON.parse(fieldValue)
                            if (!BinaryRep.isBinary(parsedJSON)) {
                                throw new StandardError("Received ResultRowNative of the wrong type")
                            }*/

                            if (fieldValue === null) {
                                if (!nullAllowed) throw new StandardError("Received non-nullable field as null")
                                // @ts-ignore
                                resultRow[fieldName] = null
                                resultRow._internalData.originalValues[fieldName] = null
                                break
                            }

                            const binary = BinaryRep.fromJSON(fieldValue)

                            // @ts-ignore
                            resultRow[fieldName] = binary
                            resultRow._internalData.originalValues[fieldName] = binary

                            break
                        }
                        default:
                            throw new StandardError("Unknown field type received")
                    }
                }

                const ret = resultRow as unknown as RowType

                await rowCallback(meta, ret)
            },
            async meta => {
                for (const fieldMeta of meta.fields) {
                    fieldTypes.set(fieldMeta.fieldName, fieldMeta.fieldType)
                }
            },
        )
    }

    protected writableObjects: PermanentObject[] = []

    Query<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
    ): QueryIterator<RowType> {
        return new QueryIterator<RowType>(allocator, querySpecification, false, this)
    }
    QueryWritable<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
    ): QueryIterator<RowType> {
        return new QueryIterator<RowType>(allocator, querySpecification, true, this)
    }

    async GetWritableObject<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
    ): Promise<RowType> {
        return this.GetObject(allocator, querySpecification, true)
    }

    clearCache(): void {
        context.request.cache.clear()
    }

    async GetObjectOrNull<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
        writable: boolean = false,
    ): Promise<RowType | null> {
        // TODO: Do we really need to create an empty object here?
        const emptyObject = new allocator()
        const requestedFields: string[] = []
        for (const [field] of Object.entries(emptyObject)) {
            requestedFields.push(field)
        }

        //        return await context.request.cache.getCachedDisabled(
        //            allocator.name + '_' + (querySpecification ?? "") + '_' + requestedFields.join(","),
        //            async () => {
        //log.info("Cache miss for " + allocator.name + " with query " + querySpecification ?? "")
        let count = 0
        let object: RowType | null = null

        for await (const obj of writable
            ? this.Query<RowType>(allocator, querySpecification)
            : this.Query<RowType>(allocator, querySpecification)) {
            if (count === 1)
                throw new StandardError(
                    "StorageContext.GetObject received more than 1 object (Query: " + querySpecification + ")",
                )
            object = obj
            count++
        }

        return object
        //            })
    }

    async GetObject<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
        writable: boolean = false,
    ): Promise<RowType> {
        const object = await this.GetObjectOrNull(allocator, querySpecification, writable)
        if (object === null)
            throw new StandardError("StorageContext.GetObject received no objects (Query: " + querySpecification + ")")

        return object
    }

    async GetObjectCached<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        querySpecification: QuerySpecification = null,
        writable: boolean = false,
    ): Promise<RowType> {
        return (await context.request.cache.get(
            allocator.name + "_" + (querySpecification ?? "") + "_" + Object.keys(new allocator()).join(","),
            async () => {
                const object = await this.GetObjectOrNull(allocator, querySpecification, writable)
                if (object === null)
                    throw new StandardError(
                        "StorageContext.GetObject received no objects (Query: " + querySpecification + ")",
                    )

                return object
            },
        )) as RowType
    }

    async CreateObject<RowType extends PermanentObject>(
        allocator: { new (): RowType },
        stringPath: string = "",
    ): Promise<RowType> {
        let count = 0
        let object: RowType | null = null

        for await (const obj of this.QueryWritable<RowType>(allocator, stringPath)) {
            if (count === 1) throw new StandardError("StorageContext.GetObject received more than 1 object")
            object = obj
            count++
        }
        if (object === null) throw new StandardError("StorageContext.GetObject received no objects")

        return object
    }

    async AddLinkedRows(selectorString: string, newPaths: Variant[]): Promise<void> {
        await context.storage.Modify({
            method: "AddLinkedRows",
            selectorString: selectorString,
            modificationRows: newPaths.map(path => {
                return { Path: path }
            }),
        })
    }

    // Only called by iterators
    AddWritableObject(item: PermanentObject): void {
        this.writableObjects.push(item)
    }

    commit(): void {
        for (const object of this.writableObjects) {
            for (const [field, fieldValue] of Object.entries(object)) {
                if (!isFieldType(fieldValue)) continue
                if (object._internalData === null)
                    throw new StandardError("Internal error: _internalData of PermamentObject was null")
                if (object._internalData.originalValues === null)
                    throw new StandardError("Internal error: _internalData.originalValues of PermamentObject was null")
                if (!Object.prototype.hasOwnProperty.call(object._internalData.originalValues, field))
                    throw new StandardError("Internal error: _internalData if PermamentObject was null")

                if (typeof object._internalData.originalValues[field] !== typeof fieldValue)
                    throw new StandardError(
                        "Internal error: PermanentObject type of field has changed which is not allow",
                    )

                if (object._internalData.originalValues[field] !== fieldValue) {
                    console.log("Field " + field + " was modified")
                }
            }
        }
    }
}

export type QuerySpecification = string | number | null

function expandQuerySpecification(querySpecification: QuerySpecification, objectInterface: string): string {
    let modifiedQuerySpecification = querySpecification
    if (modifiedQuerySpecification === null) {
        modifiedQuerySpecification = ""
    }
    // A number is assumed to be an ID
    else if (typeof modifiedQuerySpecification === "number") {
        modifiedQuerySpecification = "Path=" + modifiedQuerySpecification
    }
    // A string with only digits is assumed to be an ID
    else if (modifiedQuerySpecification.match(/^[0-9]+$/)) {
        modifiedQuerySpecification = "Path=" + modifiedQuerySpecification
    }
    // A string without an assignment is assumed to be a path
    else if (modifiedQuerySpecification.indexOf("=") === -1) {
        modifiedQuerySpecification = "Path='" + modifiedQuerySpecification + "'"
    }

    // Add Interface of not present
    // TODO: If the path or anything contains the word Interface, this is not going to work properly
    if (modifiedQuerySpecification.indexOf("Interface") === -1) {
        if (modifiedQuerySpecification.length > 0)
            modifiedQuerySpecification = 'Interface="' + objectInterface + '",' + modifiedQuerySpecification
        else modifiedQuerySpecification = 'Interface="' + objectInterface + '"'
    }

    return modifiedQuerySpecification
}

function isFieldType(value: unknown): boolean {
    if (typeof value === "string") return true
    if (typeof value === "number") return true
    if (typeof value === "object" && value instanceof TimeStamp) return true
    if (typeof value === "object" && value instanceof BinaryRep) return true
    if (value === null) return true

    return false
}

// Modify

export interface ModificationResult {
    resultingPath?: string
    resultingId?: number
    objectWasCreated?: boolean
}

export class PermanentObject {
    _internalData: InternalPermamentObjectData = new InternalPermamentObjectData()
}

export class GenericPermanentObject extends PermanentObject {
    constructor(fields: string | string[]) {
        super()
        if (typeof fields === "string") this[fields] = 0
        else {
            for (const field of fields) this[field] = 0
        }
    }
    [key: string]: unknown
}

class InternalPermamentObjectData {
    objectInterface: string | null = null
    originalValues: ResultRow = {}
}

// Folder Listing is quite typical so it's included here
export class Folder extends PermanentObject {
    Path: string = ""
    Name: string = ""
    ObjectType: string = ""
    Interfaces: string = ""
}
